import React from 'react';
import { Box, Typography, Button, Stack, Paper } from '@mui/material';
import { useAlert } from '../../hooks/useAlert';

/**
 * AlertDemo component - Demonstrates the new Toast notification system
 * 
 * This component shows how the new Toast and ConfirmDialog components
 * replace window.alert() and window.confirm() calls with better UX.
 */
const AlertDemo: React.FC = () => {
  const { showSuccess, showError, showWarning, showInfo, showConfirm } = useAlert();

  const handleImportDemo = async () => {
    // Simulate import process
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      showSuccess('Designs imported successfully!');
    } catch (error) {
      showError('Failed to import designs. Please try again.');
    }
  };

  const handleExportDemo = async () => {
    // Simulate export process
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      showSuccess('Designs exported successfully!');
    } catch (error) {
      showError('Failed to export designs. Please try again.');
    }
  };

  const handleDeleteDemo = async () => {
    // Show confirmation dialog
    const confirmed = await showConfirm({
      title: 'Delete Designs',
      message: 'Are you sure you want to delete "Conference Room A"? This action cannot be undone.',
      type: 'warning',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    
    if (confirmed) {
      try {
        // Simulate delete API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        showSuccess('Design deleted successfully!');
      } catch (error) {
        showError('Failed to delete design. Please try again.');
      }
    }
  };

  const handleWarningDemo = () => {
    showWarning('Please review your input before proceeding.');
  };

  const handleInfoDemo = () => {
    showInfo('This is some important information for you.');
  };

  return (
    <Box sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Toast Notification System Demo
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
        This demonstrates the new Toast notification system that replaces window.alert() calls with 
        non-blocking toast messages that slide in from the bottom-left corner. The system provides better user experience with:
      </Typography>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>Features:</Typography>
        <ul>
          <li>Non-blocking toast notifications that auto-dismiss</li>
          <li>Slide-in animation from bottom-left corner</li>
          <li>Different types: success, error, warning, info</li>
          <li>Confirmation dialogs for destructive actions</li>
          <li>Icons for better visual feedback</li>
          <li>Consistent Material-UI styling</li>
          <li>Accessible design</li>
        </ul>
      </Box>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Import/Export Scenarios
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          These buttons simulate the import and export functionality from the DesignsList component:
        </Typography>
        
        <Stack spacing={2} direction="row" flexWrap="wrap" gap={2}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleImportDemo}
          >
            Simulate Import Success
          </Button>
          
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleExportDemo}
          >
            Simulate Export Success
          </Button>
          
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleDeleteDemo}
          >
            Simulate Delete (with Confirmation)
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Alert Types
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          Different types of alerts for various scenarios:
        </Typography>
        
        <Stack spacing={2} direction="row" flexWrap="wrap" gap={2}>
          <Button 
            variant="outlined" 
            color="success" 
            onClick={() => showSuccess('Operation completed successfully!')}
          >
            Success Alert
          </Button>
          
          <Button 
            variant="outlined" 
            color="error" 
            onClick={() => showError('An error occurred while processing.')}
          >
            Error Alert
          </Button>
          
          <Button 
            variant="outlined" 
            color="warning" 
            onClick={handleWarningDemo}
          >
            Warning Alert
          </Button>
          
          <Button 
            variant="outlined" 
            color="info" 
            onClick={handleInfoDemo}
          >
            Info Alert
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Usage in Code
        </Typography>
        <Typography variant="body2" component="pre" sx={{ 
          fontFamily: 'monospace', 
          fontSize: '0.875rem',
          bgcolor: 'grey.100',
          p: 2,
          borderRadius: 1,
          overflow: 'auto'
        }}>
{`// Before (old way)
window.alert('Designs imported successfully!');
const confirmed = window.confirm('Delete this design?');

// After (new way with Toast notifications)
import { useAlert } from 'hooks/useAlert';

const { showSuccess, showError, showConfirm } = useAlert();

// Show success toast (auto-dismisses after 4 seconds)
showSuccess('Designs imported successfully!');

// Show error toast (auto-dismisses after 6 seconds)
showError('Failed to import designs. Please try again.');

// Show confirmation dialog (still modal for destructive actions)
const confirmed = await showConfirm({
  title: 'Delete Design',
  message: 'Are you sure you want to delete this design?',
  type: 'warning',
  confirmText: 'Delete',
  cancelText: 'Cancel'
});`}
        </Typography>
      </Paper>
    </Box>
  );
};

export default AlertDemo;
